/**
 * Hook that saves onboarding data to the API after authentication.
 *
 * `useSaveOnboardingToApi` — imperative hook returning a save function.
 * Called from CreateAccountScreen after OTP/OAuth to save before redirect.
 */
import { useCallback, useRef } from "react";
import { useOnboarding } from "../onboarding/OnboardingContext";
import { useUpdatePatient } from "@src/packages/patients/hooks/usePatient";
import { useAcceptPartnerInvite } from "@src/packages/patients/hooks/usePartnerInvite";
import { loadOnboardingData } from "../onboarding/onboardingStorage";
import type { OnboardingData } from "../onboarding/types";
import { buildOnboardingPatientPayload } from "../onboarding/onboardingPayload";

/**
 * Thrown when accept fails on a stale/invalid invitation code. The caller
 * (CreateAccountScreen) uses this to surface a user-facing error instead of
 * leaving the user stuck on the saving spinner.
 */
export type PartnerInviteFailureReason =
  "invalid" | "expired" | "already_used" | "cancelled" | "unavailable";
export type PartnerInviteFailureStage = "accept" | "profile";

function classifyPartnerInviteFailure(
  error: unknown,
): PartnerInviteFailureReason {
  const message = error instanceof Error ? error.message.toLowerCase() : "";

  if (message.includes("expired")) return "expired";
  if (message.includes("cancelled") || message.includes("canceled")) {
    return "cancelled";
  }
  if (
    message.includes("already") ||
    message.includes("accepted") ||
    message.includes("consumed") ||
    message.includes("used")
  ) {
    return "already_used";
  }
  if (
    message.includes("invalid") ||
    message.includes("malformed") ||
    message.includes("not found")
  ) {
    return "invalid";
  }
  return "unavailable";
}

export class PartnerAcceptFailedError extends Error {
  readonly reason: PartnerInviteFailureReason;
  readonly stage: PartnerInviteFailureStage;

  constructor(
    reason: PartnerInviteFailureReason,
    stage: PartnerInviteFailureStage = "accept",
  ) {
    super("The partner invitation could not be accepted.");
    this.name = "PartnerAcceptFailedError";
    this.reason = reason;
    this.stage = stage;
  }
}

export const ONBOARDING_PATIENT_SAVE_TIMEOUT_MS = 15_000;

export class OnboardingPatientSaveTimeoutError extends Error {
  constructor() {
    super("The patient record save timed out.");
    this.name = "OnboardingPatientSaveTimeoutError";
  }
}

/**
 * Check whether any saveable onboarding data exists.
 */
export function hasSaveableData(data: OnboardingData): boolean {
  return !!(
    data.firstName ||
    data.gender ||
    data.age ||
    data.zipCode ||
    data.ethnicity.length > 0 ||
    data.partnerChoice ||
    data.timeline ||
    data.treatmentIntent ||
    data.fertilityStage ||
    data.plannedTreatmentModality ||
    data.currentTreatmentFamily ||
    data.primaryIntent ||
    data.hasEmployerBenefits !== null ||
    data.employerBenefit ||
    data.partnerInvite ||
    data.periodRegularity
  );
}

/**
 * Build and fire a single patient PATCH with all onboarding data
 * (including nested fertilityIntent).
 */
function fireOnboardingSave(
  dataToSave: OnboardingData,
  stytchPhone: string | undefined,
  updatePatient: ReturnType<typeof useUpdatePatient>,
  idempotencyKey?: string,
): Promise<void> {
  const patientPayload = {
    ...buildOnboardingPatientPayload(dataToSave, stytchPhone),
    ...(idempotencyKey ? { _idempotencyKey: idempotencyKey } : {}),
  };

  if (Object.keys(patientPayload).length === 0) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      controller.abort();
      reject(new OnboardingPatientSaveTimeoutError());
    }, ONBOARDING_PATIENT_SAVE_TIMEOUT_MS);

    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve();
    };
    const rejectOnce = (error: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      reject(error);
    };

    try {
      updatePatient.mutate(
        { ...patientPayload, _signal: controller.signal },
        {
          onSuccess: resolveOnce,
          onError: (err) => {
            console.error(
              "[useSaveOnboardingData] Failed to save patient data:",
              err,
            );
            rejectOnce(err);
          },
        },
      );
    } catch (error) {
      rejectOnce(error);
    }
  });
}

/**
 * Imperative hook — returns onboarding data and a save function.
 * The caller passes data and phone at the call site, keeping the
 * callback reference stable (no dependency on onboardingData or stytchUser).
 */
export function useSaveOnboardingToApi() {
  const { data, clearData } = useOnboarding();
  const updatePatient = useUpdatePatient();
  const acceptPartnerInvite = useAcceptPartnerInvite();
  const acceptedInvitationCodes = useRef(new Set<string>());

  const acceptInvite = useCallback(
    async (invitationCode: string) => {
      try {
        const result = await acceptPartnerInvite.mutateAsync(invitationCode);
        acceptedInvitationCodes.current.add(invitationCode);
        return result;
      } catch (err) {
        throw new PartnerAcceptFailedError(classifyPartnerInviteFailure(err));
      }
    },
    [acceptPartnerInvite],
  );

  const save = useCallback(
    async (
      onboardingData: OnboardingData,
      stytchPhone: string | undefined,
      idempotencyKey?: string,
    ) => {
      let dataToSave = onboardingData;
      if (!hasSaveableData(dataToSave)) {
        const stored = await loadOnboardingData();
        if (stored) dataToSave = stored;
      }

      const invitationCode = dataToSave.partnerInvite?.code;

      // Validate and consume the invitation before mutating the invitee's
      // profile. A stale link must never leave behind a partial partner-mode
      // profile. If the subsequent PATCH fails, the recovery action retries
      // the same idempotent accept operation and the profile save together.
      if (
        invitationCode &&
        !acceptedInvitationCodes.current.has(invitationCode)
      ) {
        await acceptInvite(invitationCode);
      }

      try {
        if (hasSaveableData(dataToSave) || stytchPhone) {
          await fireOnboardingSave(
            dataToSave,
            stytchPhone,
            updatePatient,
            idempotencyKey,
          );
        }
      } catch (err) {
        if (invitationCode) {
          throw new PartnerAcceptFailedError("unavailable", "profile");
        }
        throw err;
      }

      // In partner mode the next screen (PartnerJourneyConfirm) still needs
      // the linked partner query, so defer clearing onboarding state until
      // that screen confirms.
      if (hasSaveableData(dataToSave) && !dataToSave.partnerInvite) {
        await clearData();
      }
    },
    [updatePatient, acceptInvite, clearData],
  );

  const retryPartnerInvite = useCallback(
    async (stytchPhone?: string) => {
      const invitationCode = data.partnerInvite?.code;
      if (!invitationCode) {
        throw new PartnerAcceptFailedError("invalid");
      }
      if (!acceptedInvitationCodes.current.has(invitationCode)) {
        await acceptInvite(invitationCode);
      }
      await fireOnboardingSave(data, stytchPhone, updatePatient);
    },
    [acceptInvite, data, updatePatient],
  );

  return { data, save, retryPartnerInvite, clearOnboarding: clearData };
}
